import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@common-fe/common-fe';
import { uniqueId } from 'lodash';

import Permissions from '@/common/permissions';
import { useHasAccess } from '@/modules/core/hooks';
import useGetEmployer from '@/modules/employer/hooks/useGetEmployer.query';
import { useSetupEmployerStore } from '@/modules/employer/store';

import AddModalWrapper from '../AddModalWrapper';

import AddEditContactsSecurityModal from './AddEditContactsSecurityModal/AddEditContactsSecurityModal';
import VerificationLinkSentModal from './AddEditContactsSecurityModal/components/VerificationLinkSentModal';
import { ContactResponseModel } from './AddEditContactsSecurityModal/ContactSecurity.types';
import { useContactListQuery } from './AddEditContactsSecurityModal/queries/useContacts.queries';
import ContactsSecurityTable from './ContactsSecurityTable/ContactsSecurityTable';
import useVerificationModalStore from './stores/useVerificationModal.store';

interface Props {
  disabled?: boolean;
  organizationId?: string;
}
const CONFIRM_CANCEL_TEXT = 'Changes that you made may not be saved.';

const ContactsSecurity: React.FC<Props> = ({ disabled, organizationId }) => {
  const { hasOneContact, setContactMap } = useSetupEmployerStore();
  const { formattedData } = useGetEmployer();
  const { contactList, refetch } = useContactListQuery({
    organizationId,
    enabled: !hasOneContact,
    size: 1000,
  });
  const [triggerRefetching, setTriggerRefetching] = useState('');
  const [openAddModal, setOpenAddModal] = useState(false);
  const [editContact, setEditContact] = useState<ContactResponseModel | undefined>();
  const [viewContactMode, setViewContactMode] = useState<boolean>(false);
  const [shouldConfirmOnClose, setShouldConfirmOnClose] = useState(false);

  const [justCreatedLabel, setJustCreatedLabel] = useState(false);
  const justCreatedLabelTimer = useRef<NodeJS.Timeout>(null);

  const isAddContactAvailable = useHasAccess([
    {
      permission: Permissions.ADD_CONTACTS,
    },
  ]);
  const isViewContactsAvailable = useHasAccess([
    {
      permission: Permissions.VIEW_CONTACT,
    },
  ]);

  const onSubmitHandler = useCallback(async () => {
    refetch();
    setJustCreatedLabel(false);
    if (justCreatedLabelTimer.current) {
      clearTimeout(justCreatedLabelTimer.current);
    }
    if (!editContact) {
      setJustCreatedLabel(true);
      justCreatedLabelTimer.current = setTimeout(() => {
        setJustCreatedLabel(false);
      }, 5000);
    }
    setEditContact(undefined);
    setOpenAddModal(false);
    setTriggerRefetching(uniqueId());
  }, [editContact, refetch]);

  const onSetVisibleHandler = useCallback(
    (value: boolean) => {
      let confirm = true;
      if (!value && !viewContactMode && shouldConfirmOnClose) {
        confirm = window.confirm(CONFIRM_CANCEL_TEXT);
      }
      if (confirm) {
        setEditContact(undefined);
        setViewContactMode(false);
        setOpenAddModal(value);
      }
    },
    [shouldConfirmOnClose, viewContactMode]
  );

  useEffect(() => {
    if (contactList.length) {
      setContactMap(contactList.reduce((map, contact) => ({ ...map, [contact.id]: contact }), {}));
    }
  }, [contactList, setContactMap]);

  const {
    isVerificationModalVisible,
    firstName,
    lastName,
    email,
    resetVerificationModalStore,
  } = useVerificationModalStore();

  useEffect(() => {
    return () => {
      resetVerificationModalStore();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box border={{ color: 'border2', size: 'small' }} background="canvas" round="container1Round">
      <AddModalWrapper
        id="contact_information"
        title="Contact Information"
        visible={openAddModal}
        onSetVisible={onSetVisibleHandler}
        buttonText="Add New Contact"
        disabled={disabled || !isAddContactAvailable}
        modalTitle={editContact ? `${editContact.first_name} ${editContact.last_name}` : 'Add New Contact'}
        successTextShow={justCreatedLabel}
        successText="Contact added."
        isClosingByClickOutsideBlocked
      >
        <AddEditContactsSecurityModal
          data={editContact}
          open
          disabledEdit={!isAddContactAvailable || disabled}
          onDirty={setShouldConfirmOnClose}
          firstContact={!hasOneContact}
          onCancel={() => onSetVisibleHandler(false)}
          onSubmit={onSubmitHandler}
          viewMode={viewContactMode}
          setViewContactMode={setViewContactMode}
          adminSsoOnly={formattedData?.overridableProperties?.adminSsoOnly}
        />
      </AddModalWrapper>

      {hasOneContact ? (
        <ContactsSecurityTable
          disabled={disabled}
          isViewContactsAvailable={isViewContactsAvailable}
          disabledEdit={!isAddContactAvailable}
          organizationId={organizationId}
          onView={(contact) => {
            setViewContactMode(true);
            setEditContact(contact);
            setOpenAddModal(true);
          }}
          onEdit={(contact) => {
            setEditContact(contact);
            setOpenAddModal(true);
            setViewContactMode(false);
          }}
          triggerRefetching={triggerRefetching}
        />
      ) : null}

      {openAddModal && (
        <AddEditContactsSecurityModal
          data={editContact}
          open
          onDirty={setShouldConfirmOnClose}
          firstContact={!hasOneContact}
          onCancel={() => onSetVisibleHandler(false)}
          onSubmit={onSubmitHandler}
          viewMode={viewContactMode}
          setViewContactMode={setViewContactMode}
          adminSsoOnly={formattedData?.overridableProperties?.adminSsoOnly}
        />
      )}

      {isVerificationModalVisible ? (
        <VerificationLinkSentModal
          onSetVisible={resetVerificationModalStore}
          email={email}
          firstName={firstName}
          lastName={lastName}
        />
      ) : null}
    </Box>
  );
};

export default ContactsSecurity;
